import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './interventions.css';

const StudentPortal = ({ studentId }) => {
    const [studentData, setStudentData] = useState(null);
    const [riskProfile, setRiskProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState('overview');

    useEffect(() => {
        if (studentId) {
            fetchStudentData();
        }
    }, [studentId]);

    const fetchStudentData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/interventions/students/${studentId}/risk/`, {
                withCredentials: true
            });
            
            if (response.data.success) {
                setRiskProfile(response.data.data.profile);
                setStudentData(response.data.data);
            } else {
                setError('Failed to load student data');
            }
        } catch (err) {
            console.error('Error fetching student data:', err);
            setError('Error loading student data');
        } finally {
            setLoading(false);
        }
    };

    const getRiskLevelColor = (riskLevel) => {
        const colors = {
            'low': '#10B981',
            'moderate': '#F59E0B',
            'high': '#F97316',
            'critical': '#EF4444'
        };
        return colors[riskLevel] || '#6B7280';
    };

    const getSeverityColor = (severity) => {
        const colors = {
            'low': '#10B981',
            'medium': '#F59E0B',
            'high': '#F97316',
            'critical': '#EF4444'
        };
        return colors[severity] || '#6B7280';
    };

    if (loading) {
        return (
            <div className="student-portal">
                <div className="loading-spinner">Loading student information...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="student-portal">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    if (!riskProfile) {
        return (
            <div className="student-portal">
                <div className="error-message">No student data found</div>
            </div>
        );
    }

    return (
        <div className="student-portal">
            <div className="student-header">
                <div className="student-info">
                    <h1>{riskProfile.student.full_name}</h1>
                    <p className="grade-info">Grade {riskProfile.student.grade_level}</p>
                </div>
                <div className="risk-status">
                    <span 
                        className="risk-badge large"
                        style={{ backgroundColor: getRiskLevelColor(riskProfile.overall_risk_level) }}
                    >
                        {riskProfile.overall_risk_level.toUpperCase()} RISK
                    </span>
                </div>
            </div>

            <div className="portal-tabs">
                <button 
                    className={`tab-button ${selectedTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'attendance' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('attendance')}
                >
                    Attendance
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'alerts' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('alerts')}
                >
                    Recent Alerts ({studentData.recent_alerts.length})
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'cases' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('cases')}
                >
                    Active Cases ({studentData.active_cases.length})
                </button>
            </div>

            <div className="portal-content">
                {selectedTab === 'overview' && (
                    <OverviewTab riskProfile={riskProfile} getRiskLevelColor={getRiskLevelColor} />
                )}
                
                {selectedTab === 'attendance' && (
                    <AttendanceTab riskProfile={riskProfile} />
                )}
                
                {selectedTab === 'alerts' && (
                    <AlertsTab alerts={studentData.recent_alerts} getSeverityColor={getSeverityColor} />
                )}
                
                {selectedTab === 'cases' && (
                    <CasesTab cases={studentData.active_cases} getSeverityColor={getSeverityColor} />
                )}
            </div>
        </div>
    );
};

const OverviewTab = ({ riskProfile, getRiskLevelColor }) => {
    return (
        <div className="overview-tab">
            <div className="overview-grid">
                <div className="overview-card">
                    <h3>Risk Assessment</h3>
                    <div className="risk-breakdown">
                        <div className="risk-item">
                            <span className="risk-label">Overall Risk:</span>
                            <span 
                                className="risk-value"
                                style={{ color: getRiskLevelColor(riskProfile.overall_risk_level) }}
                            >
                                {riskProfile.overall_risk_level.toUpperCase()}
                            </span>
                        </div>
                        <div className="risk-item">
                            <span className="risk-label">Attendance Risk:</span>
                            <span 
                                className="risk-value"
                                style={{ color: getRiskLevelColor(riskProfile.attendance_risk) }}
                            >
                                {riskProfile.attendance_risk.toUpperCase()}
                            </span>
                        </div>
                        <div className="risk-item">
                            <span className="risk-label">Academic Risk:</span>
                            <span 
                                className="risk-value"
                                style={{ color: getRiskLevelColor(riskProfile.academic_risk) }}
                            >
                                {riskProfile.academic_risk.toUpperCase()}
                            </span>
                        </div>
                        <div className="risk-item">
                            <span className="risk-label">Behavioral Risk:</span>
                            <span 
                                className="risk-value"
                                style={{ color: getRiskLevelColor(riskProfile.behavioral_risk) }}
                            >
                                {riskProfile.behavioral_risk.toUpperCase()}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="overview-card">
                    <h3>Key Metrics</h3>
                    <div className="metrics-grid">
                        <div className="metric-item">
                            <div className="metric-value">{riskProfile.attendance_rate_30_days}%</div>
                            <div className="metric-label">30-Day Attendance</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">{riskProfile.consecutive_absences}</div>
                            <div className="metric-label">Consecutive Absences</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">{riskProfile.total_alerts_30_days}</div>
                            <div className="metric-label">Recent Alerts</div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value">{riskProfile.active_cases_count}</div>
                            <div className="metric-label">Active Cases</div>
                        </div>
                    </div>
                </div>

                <div className="overview-card full-width">
                    <h3>Current Risk Factors</h3>
                    <div className="risk-factors-list">
                        {riskProfile.risk_summary.map((factor, index) => (
                            <div key={index} className="risk-factor-item">
                                <span className="factor-icon">⚠️</span>
                                <span className="factor-text">{factor}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AttendanceTab = ({ riskProfile }) => {
    return (
        <div className="attendance-tab">
            <div className="attendance-summary">
                <h3>Attendance Summary (Last 30 Days)</h3>
                <div className="attendance-stats">
                    <div className="attendance-stat">
                        <div className="stat-circle" style={{ 
                            background: `conic-gradient(#10B981 0deg ${riskProfile.attendance_rate_30_days * 3.6}deg, #E5E7EB ${riskProfile.attendance_rate_30_days * 3.6}deg 360deg)` 
                        }}>
                            <span className="stat-percentage">{riskProfile.attendance_rate_30_days}%</span>
                        </div>
                        <div className="stat-label">Attendance Rate</div>
                    </div>
                    
                    <div className="attendance-details">
                        <div className="detail-item">
                            <span className="detail-label">Consecutive Absences:</span>
                            <span className="detail-value">{riskProfile.consecutive_absences} days</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Last Updated:</span>
                            <span className="detail-value">
                                {new Date(riskProfile.last_updated).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="attendance-recommendations">
                <h4>Recommendations</h4>
                {riskProfile.attendance_rate_30_days < 90 ? (
                    <div className="recommendation warning">
                        <p>Your attendance rate is below the recommended 90%. Consider:</p>
                        <ul>
                            <li>Speaking with your homeroom teacher about any challenges</li>
                            <li>Setting up a morning routine to help with punctuality</li>
                            <li>Identifying any barriers to regular attendance</li>
                        </ul>
                    </div>
                ) : (
                    <div className="recommendation success">
                        <p>Great job maintaining good attendance! Keep up the excellent work.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const AlertsTab = ({ alerts, getSeverityColor }) => {
    return (
        <div className="alerts-tab">
            <h3>Recent Alerts (Last 30 Days)</h3>
            {alerts.length === 0 ? (
                <div className="empty-state">No recent alerts</div>
            ) : (
                <div className="student-alerts-list">
                    {alerts.map(alert => (
                        <div key={alert.id} className="student-alert-card">
                            <div className="alert-header">
                                <span 
                                    className="severity-badge"
                                    style={{ backgroundColor: getSeverityColor(alert.severity) }}
                                >
                                    {alert.severity.toUpperCase()}
                                </span>
                                <span className="alert-date">
                                    {new Date(alert.trigger_date).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="alert-title">{alert.title}</div>
                            <div className="alert-description">{alert.description}</div>
                            <div className="alert-status">
                                Status: <span className="status-value">{alert.status}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const CasesTab = ({ cases, getSeverityColor }) => {
    return (
        <div className="cases-tab">
            <h3>Active Intervention Cases</h3>
            {cases.length === 0 ? (
                <div className="empty-state">No active cases</div>
            ) : (
                <div className="student-cases-list">
                    {cases.map(case_ => (
                        <div key={case_.id} className="student-case-card">
                            <div className="case-header">
                                <span 
                                    className="priority-badge"
                                    style={{ backgroundColor: getSeverityColor(case_.priority) }}
                                >
                                    {case_.priority.toUpperCase()}
                                </span>
                                <span className="case-type">{case_.case_type}</span>
                            </div>
                            <div className="case-title">{case_.title}</div>
                            <div className="case-description">{case_.description}</div>
                            <div className="case-progress">
                                <div className="progress-item">
                                    <span className="progress-label">Days Open:</span>
                                    <span className="progress-value">{case_.days_open}</span>
                                </div>
                                <div className="progress-item">
                                    <span className="progress-label">Actions Completed:</span>
                                    <span className="progress-value">
                                        {case_.completed_action_count}/{case_.action_count}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StudentPortal;
